/**
 * USNI Fleet Tracker source — scrapes the USNI News Fleet Tracker RSS feed
 * for carrier strike group deployment positions.
 *
 * The USNI Fleet Tracker publishes weekly summaries of US Navy carrier and
 * amphibious ready group positions. This source extracts those positions and
 * geo-codes them using known maritime region coordinates.
 *
 * Update interval: 6 hours (fleet tracker updates weekly, but we check more
 * frequently to catch new posts promptly).
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, EntityReference } from '../base-source';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'usni';

// ---------------------------------------------------------------------------
// Maritime region coordinates for geo-coding location descriptions
// ---------------------------------------------------------------------------

interface MaritimeRegion {
  lat: number;
  lng: number;
  keywords: string[];
}

/**
 * Map of maritime regions/areas to coordinates. When a fleet tracker article
 * mentions one of these regions, the corresponding coordinates are used.
 * Coordinates represent approximate center of each operational area.
 */
const MARITIME_REGIONS: MaritimeRegion[] = [
  // --- Oceans & Seas ---
  { lat: 25.0, lng: -45.0, keywords: ['atlantic', 'north atlantic'] },
  { lat: -15.0, lng: -25.0, keywords: ['south atlantic'] },
  { lat: 20.0, lng: -160.0, keywords: ['pacific', 'central pacific'] },
  { lat: 35.0, lng: 140.0, keywords: ['western pacific', 'west pacific', 'westpac'] },
  { lat: 10.0, lng: -120.0, keywords: ['eastern pacific', 'east pacific', 'eastpac'] },
  { lat: 15.0, lng: 65.0, keywords: ['indian ocean'] },
  { lat: 75.0, lng: 0.0, keywords: ['arctic ocean', 'arctic'] },

  // --- Key operational areas ---
  { lat: 26.0, lng: 52.0, keywords: ['persian gulf', 'arabian gulf'] },
  { lat: 14.0, lng: 42.5, keywords: ['red sea', 'bab el-mandeb', 'bab al-mandeb'] },
  { lat: 12.0, lng: 44.0, keywords: ['gulf of aden'] },
  { lat: 35.5, lng: 18.0, keywords: ['mediterranean', 'med sea', '6th fleet'] },
  { lat: 12.0, lng: 114.0, keywords: ['south china sea', 'scs'] },
  { lat: 26.0, lng: 125.0, keywords: ['east china sea'] },
  { lat: 38.5, lng: 131.0, keywords: ['sea of japan', 'east sea'] },
  { lat: 58.0, lng: 20.0, keywords: ['baltic sea', 'baltic'] },
  { lat: 44.0, lng: 35.0, keywords: ['black sea'] },
  { lat: 24.5, lng: 119.5, keywords: ['taiwan strait', 'formosa strait'] },
  { lat: 2.5, lng: 101.0, keywords: ['strait of malacca', 'malacca strait'] },
  { lat: 30.5, lng: 32.3, keywords: ['suez canal', 'suez'] },
  { lat: 36.0, lng: -5.5, keywords: ['strait of gibraltar', 'gibraltar'] },
  { lat: 8.0, lng: -80.0, keywords: ['panama canal'] },
  { lat: 27.0, lng: 56.3, keywords: ['strait of hormuz', 'hormuz'] },
  { lat: 5.0, lng: 47.0, keywords: ['horn of africa', 'somali coast'] },

  // --- US Fleet areas ---
  { lat: 36.8, lng: -76.3, keywords: ['norfolk', 'hampton roads', 'east coast'] },
  { lat: 32.7, lng: -117.2, keywords: ['san diego', 'west coast'] },
  { lat: 21.4, lng: -158.0, keywords: ['pearl harbor', 'hawaii'] },
  { lat: 35.3, lng: 139.7, keywords: ['yokosuka', '7th fleet'] },
  { lat: 41.4, lng: -72.1, keywords: ['groton', 'new london'] },
  { lat: 30.4, lng: -87.2, keywords: ['pensacola'] },
  { lat: 36.9, lng: -76.0, keywords: ['2nd fleet'] },
  { lat: 25.9, lng: 50.6, keywords: ['bahrain', '5th fleet', 'navcent'] },
  { lat: 32.7, lng: -117.2, keywords: ['3rd fleet'] },
  { lat: 47.6, lng: -122.7, keywords: ['bremerton', 'puget sound'] },

  // --- Allied / partner ports ---
  { lat: 36.6, lng: -6.4, keywords: ['rota', 'naval station rota'] },
  { lat: 40.8, lng: 14.3, keywords: ['naples', 'gaeta'] },
  { lat: 1.3, lng: 103.8, keywords: ['singapore', 'changi'] },
  { lat: 11.1, lng: 78.0, keywords: ['india', 'bay of bengal'] },
  { lat: 25.3, lng: 55.3, keywords: ['dubai', 'uae'] },
  { lat: 33.2, lng: 129.7, keywords: ['sasebo'] },
  { lat: 34.1, lng: 132.2, keywords: ['iwakuni'] },
  { lat: 35.1, lng: 129.1, keywords: ['busan', 'south korea'] },
  { lat: 51.4, lng: 1.4, keywords: ['channel', 'english channel'] },
  { lat: 68.0, lng: 15.0, keywords: ['norwegian sea', 'norway'] },
  { lat: 70.0, lng: 33.0, keywords: ['barents sea'] },
];

/**
 * Attempt to geo-code a text description to lat/lng by matching
 * against known maritime regions.
 */
function geocodeFleetLocation(text: string): { lat: number; lng: number; name: string } | null {
  const lower = text.toLowerCase();

  for (const region of MARITIME_REGIONS) {
    for (const keyword of region.keywords) {
      if (lower.includes(keyword)) {
        return { lat: region.lat, lng: region.lng, name: keyword };
      }
    }
  }

  return null;
}

/**
 * Extract ship/group names from article text.
 * Looks for patterns like "USS <Name> (CVN-XX)" or "Carrier Strike Group X".
 */
function extractFleetEntities(text: string): EntityReference[] {
  const entities: EntityReference[] = [];
  const seen = new Set<string>();

  // Match "USS Name (HULL-XX)" patterns
  const ussPattern = /USS\s+([A-Z][a-zA-Z\s.]+?)\s*\(([A-Z]+-\d+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = ussPattern.exec(text)) !== null) {
    const name = `USS ${match[1].trim()}`;
    const hull = match[2];
    const key = hull.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      entities.push({
        entityId: `usni-vessel-${djb2Hash(key)}`,
        name: `${name} (${hull})`,
        type: 'vessel',
        confidence: 0.95,
      });
    }
  }

  // Match "Carrier Strike Group X" / "Amphibious Ready Group"
  const groupPattern = /((?:Carrier Strike Group|Amphibious Ready Group|Expeditionary Strike Group)\s*\d*)/gi;
  while ((match = groupPattern.exec(text)) !== null) {
    const name = match[1].trim();
    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      entities.push({
        entityId: `usni-group-${djb2Hash(key)}`,
        name,
        type: 'military-unit',
        confidence: 0.9,
      });
    }
  }

  return entities;
}

// ---------------------------------------------------------------------------
// Simple RSS XML parser (no external dependency)
// ---------------------------------------------------------------------------

interface RssItem {
  title: string;
  link: string;
  description: string;
  pubDate: string;
  guid: string;
}

function parseRssXml(xml: string): RssItem[] {
  const items: RssItem[] = [];

  // Extract <item>...</item> blocks
  const itemPattern = /<item>([\s\S]*?)<\/item>/gi;
  let itemMatch: RegExpExecArray | null;

  while ((itemMatch = itemPattern.exec(xml)) !== null) {
    const block = itemMatch[1];

    const getTag = (tag: string): string => {
      // Handle CDATA wrapped content
      const cdataPattern = new RegExp(`<${tag}>\\s*<!\\[CDATA\\[([\\s\\S]*?)\\]\\]>\\s*</${tag}>`, 'i');
      const cdataMatch = cdataPattern.exec(block);
      if (cdataMatch) return cdataMatch[1].trim();

      const simplePattern = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, 'i');
      const simpleMatch = simplePattern.exec(block);
      return simpleMatch ? simpleMatch[1].trim() : '';
    };

    const title = getTag('title');
    const link = getTag('link');
    const description = getTag('description');
    const pubDate = getTag('pubDate');
    const guid = getTag('guid') || link;

    if (title && link) {
      items.push({ title, link, description, pubDate, guid });
    }
  }

  return items;
}

/**
 * Strip HTML tags from a string, returning plain text.
 */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").trim();
}

// ---------------------------------------------------------------------------
// Source implementation
// ---------------------------------------------------------------------------

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const usniSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'USNI Fleet Tracker',
    description: 'US Naval Institute Fleet Tracker — carrier strike group deployment positions',
    icon: 'anchor',
    category: 'maritime',
    updateInterval: 21_600_000, // 6 hours
    requiresApiKey: false,
    dataLicense: 'USNI News (Fair Use)',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] USNI Fleet Tracker source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/usni`;
      const response = await fetch(url, { signal: AbortSignal.timeout(20_000) });

      if (!response.ok) {
        throw new Error(`USNI proxy returned ${response.status}`);
      }

      const xml = await response.text();
      const items = parseRssXml(xml);

      if (items.length === 0) {
        status.state = 'active';
        status.lastFetch = new Date().toISOString();
        status.eventCount = 0;
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const item of items) {
        // Only process fleet tracker articles (filter by title patterns)
        const titleLower = item.title.toLowerCase();
        const isFleetTracker =
          titleLower.includes('fleet tracker') ||
          titleLower.includes('carrier strike group') ||
          titleLower.includes('amphibious ready group') ||
          titleLower.includes('deployed') ||
          titleLower.includes('deployment') ||
          titleLower.includes('naval forces');

        if (!isFleetTracker) continue;

        // Parse publication date
        let timestamp: string;
        try {
          timestamp = new Date(item.pubDate).toISOString();
        } catch {
          timestamp = new Date().toISOString();
        }

        const plainDescription = stripHtml(item.description);
        const combinedText = `${item.title} ${plainDescription}`;

        // Extract entities (ships, groups)
        const entities = extractFleetEntities(combinedText);

        // Geo-code from text content
        const location = geocodeFleetLocation(combinedText);

        const event: NormalizedEvent = {
          id: `usni-${djb2Hash(item.guid)}`,
          source: SOURCE_ID,
          timestamp,
          type: 'military',
          title: item.title,
          description: plainDescription.slice(0, 500),
          severity: 'info',
          confidence: 0.85,
          entities,
          tags: ['usni', 'fleet-tracker', 'navy', 'deployment'],
          links: [{ url: item.link, label: 'USNI News' }],
          raw: {
            guid: item.guid,
            pubDate: item.pubDate,
          },
        };

        if (location) {
          event.location = {
            lat: location.lat,
            lng: location.lng,
            name: location.name,
          };
        }

        events.push(event);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
